import type { ApiError } from '@waldermarch/shared';

const BASE = import.meta.env.VITE_API_URL ?? '';
const TOKEN_KEY = 'waldermarch.token';

export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/**
 * The session cookie that lets the forum subdomain share a sign-in.
 *
 * localStorage is an island per origin: a player signed in on
 * waldermarch.com landing on forum.waldermarch.com would be a stranger
 * there. A cookie scoped to the parent domain is readable by both, so the
 * token is mirrored into one whenever the site is running on the real
 * domain, and read back as a fallback wherever localStorage comes up empty.
 * Not HttpOnly by necessity - the client attaches it as a bearer header
 * itself - which is exactly the exposure localStorage already had.
 */
const TOKEN_COOKIE = 'wm_token';

function onGameDomain(): boolean {
  return location.hostname.endsWith('waldermarch.com');
}

function cookieToken(): string | null {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${TOKEN_COOKIE}=`));
  return hit ? decodeURIComponent(hit.slice(TOKEN_COOKIE.length + 1)) || null : null;
}

function setCookieToken(token: string | null): void {
  if (!onGameDomain()) return;
  const base = `${TOKEN_COOKIE}=${token ? encodeURIComponent(token) : ''}; Domain=.waldermarch.com; Path=/; SameSite=Lax; Secure`;
  document.cookie = token ? `${base}; Max-Age=${60 * 60 * 24 * 30}` : `${base}; Max-Age=0`;
}

export function getToken(): string | null {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) return stored;
  // Signed in on the other subdomain: adopt the shared cookie session.
  const shared = onGameDomain() ? cookieToken() : null;
  if (shared) localStorage.setItem(TOKEN_KEY, shared);
  return shared;
}

export function setToken(token: string | null): void {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
  setCookieToken(token);
}

/** Called when the server rejects our token, so the app can bounce to login. */
let onUnauthorised: (() => void) | null = null;
export function setUnauthorisedHandler(handler: () => void): void {
  onUnauthorised = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError('NETWORK', 'Could not reach the server.', 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiError>;
    if (response.status === 401) {
      setToken(null);
      onUnauthorised?.();
    }
    throw new ApiRequestError(
      error.code ?? 'UNKNOWN',
      error.error ?? `Request failed (${response.status})`,
      response.status,
      error.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
